<template>
  <div>
  <ai-tab-nav />
  <div class="ai-audit-task-page">
    <page-header title="AI 审核任务" :count="total">
      <el-select v-model="filter.status" placeholder="任务状态" clearable size="small" @change="loadList">
        <el-option label="PENDING" value="PENDING" />
        <el-option label="RUNNING" value="RUNNING" />
        <el-option label="SUCCESS" value="SUCCESS" />
        <el-option label="FAILED" value="FAILED" />
      </el-select>
      <el-select v-model="filter.overallStatus" placeholder="整体结果" clearable size="small" @change="loadList">
        <el-option label="PASS" value="PASS" />
        <el-option label="WARN" value="WARN" />
        <el-option label="REJECT" value="REJECT" />
      </el-select>
      <el-input v-model="filter.bizType" placeholder="bizType" clearable size="small" @keyup.enter.native="loadList" />
      <el-input v-model="filter.bizId" placeholder="bizId" clearable size="small" @keyup.enter.native="loadList" />
      <el-button size="small" icon="el-icon-search" type="primary" @click="loadList">查询</el-button>
      <el-button size="small" icon="el-icon-refresh" @click="loadList">刷新</el-button>
    </page-header>

    <el-table :data="list" v-loading="loading" stripe border size="small" class="app-table" style="width:100%">
      <el-table-column prop="id" label="ID" width="60" align="center" />
      <el-table-column prop="bizType" label="业务类型" width="100" align="center" />
      <el-table-column prop="bizId" label="业务ID" width="100" align="center" />
      <el-table-column prop="sceneCode" label="场景" width="90" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini">{{ row.sceneCode }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="feeTypes" label="费用类型" width="150" show-overflow-tooltip>
        <template slot-scope="{ row }">{{ formatFeeTypes(row.feeTypes) }}</template>
      </el-table-column>
      <el-table-column prop="status" label="任务状态" width="90" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="statusType(row.status)">{{ row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="overallStatus" label="整体" width="80" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="overallType(row.overallStatus)" v-if="row.overallStatus">{{ row.overallStatus }}</el-tag>
          <span v-else style="color:#bbb">-</span>
        </template>
      </el-table-column>
      <el-table-column label="统计" width="160" align="center">
        <template slot-scope="{ row }">
          <span style="color:#67C23A">通过{{ row.passCount || 0 }}</span> /
          <span style="color:#F56C6C">失败{{ row.failCount || 0 }}</span> /
          <span style="color:#E6A23C">警告{{ row.warnCount || 0 }}</span>
          <span style="color:#999"> (共{{ row.totalCount || 0 }})</span>
        </template>
      </el-table-column>
      <el-table-column prop="costMs" label="耗时" width="80" align="center">
        <template slot-scope="{ row }">{{ row.costMs ? (row.costMs / 1000).toFixed(1) + 's' : '-' }}</template>
      </el-table-column>
      <el-table-column label="Tokens" width="120" align="center">
        <template slot-scope="{ row }">
          <span v-if="row.promptTokens">in:{{ row.promptTokens }} / out:{{ row.completionTokens }}</span>
          <span v-else style="color:#bbb">-</span>
        </template>
      </el-table-column>
      <el-table-column label="重试" width="70" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="row.retryCount > 0" size="mini" type="warning">{{ row.retryCount }} 次</el-tag>
          <span v-else style="color:#67C23A">0</span>
        </template>
      </el-table-column>
      <el-table-column label="模型" width="110" align="center">
        <template slot-scope="{ row }">
          <el-tag v-if="isVisionTask(row)" size="mini" type="success">👁 Vision</el-tag>
          <el-tag v-else-if="row.llmConfigId" size="mini">文本</el-tag>
          <span v-else style="color:#bbb">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="提交时间" min-width="160" />
      <el-table-column label="操作" width="100" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="openDetail(row.id)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="app-pagination">
      <el-pagination
        background
        layout="prev, pager, next, total"
        :total="total"
        :current-page.sync="page.pageNum"
        :page-size="page.pageSize"
        @current-change="loadList" />
    </div>

    <!-- 详情抽屉 -->
    <el-drawer title="审核任务详情" :visible.sync="detailVisible" size="80%" :before-close="closeDetail">
      <div class="detail-body" v-if="detail">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="基本信息" name="info">
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="任务ID">{{ detail.id }}</el-descriptions-item>
              <el-descriptions-item label="任务编号">{{ detail.taskNo }}</el-descriptions-item>
              <el-descriptions-item label="业务类型">{{ detail.bizType }}</el-descriptions-item>
              <el-descriptions-item label="业务ID">{{ detail.bizId }}</el-descriptions-item>
              <el-descriptions-item label="场景">{{ detail.sceneCode }}</el-descriptions-item>
              <el-descriptions-item label="费用类型">{{ formatFeeTypes(detail.feeTypes) }}</el-descriptions-item>
              <el-descriptions-item label="任务状态">
                <el-tag size="mini" :type="statusType(detail.status)">{{ detail.status }}</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="整体结果">
                <el-tag size="mini" :type="overallType(detail.overallStatus)" v-if="detail.overallStatus">{{ detail.overallStatus }}</el-tag>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="开始时间">{{ detail.startTime || '-' }}</el-descriptions-item>
              <el-descriptions-item label="结束时间">{{ detail.endTime || '-' }}</el-descriptions-item>
              <el-descriptions-item label="耗时">{{ detail.costMs ? (detail.costMs / 1000).toFixed(1) + ' 秒' : '-' }}</el-descriptions-item>
              <el-descriptions-item label="Tokens">
                <span v-if="detail.promptTokens">输入 {{ detail.promptTokens }} / 输出 {{ detail.completionTokens }}</span>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="重试次数">
                <span :style="{ color: detail.retryCount > 0 ? '#E6A23C' : '#67C23A' }">
                  {{ detail.retryCount || 0 }}
                </span>
              </el-descriptions-item>
              <el-descriptions-item label="检查项总数">{{ detail.totalCount }}</el-descriptions-item>
              <el-descriptions-item label="使用模型">
                <span v-if="llmSnapInfo.model" style="font-family:monospace;font-size:12px">
                  {{ llmSnapInfo.model }}
                </span>
                <span v-else style="color:#bbb">-</span>
                <el-tag v-if="llmSnapInfo.supportsVision === 1" type="success" size="mini" style="margin-left:8px">👁 Vision</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="结果分布" :span="2">
                通过 {{ detail.passCount }} / 失败 {{ detail.failCount }} / 警告 {{ detail.warnCount }}
              </el-descriptions-item>
              <el-descriptions-item label="错误信息" :span="2" v-if="detail.errorMsg">
                <pre style="margin:0;color:#F56C6C">{{ detail.errorMsg }}</pre>
              </el-descriptions-item>
            </el-descriptions>
          </el-tab-pane>

          <el-tab-pane :label="`违规明细 (${results.length})`" name="results">
            <el-table :data="results" size="small" border stripe>
              <el-table-column prop="itemCode" label="检查项编号" width="110" />
              <el-table-column prop="ruleCode" label="规则" width="80" />
              <el-table-column prop="status" label="状态" width="80" align="center">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="row.status === 'WARN' ? 'warning' : 'danger'">{{ row.status }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="feeType" label="费用类型" width="100" />
              <el-table-column prop="docType" label="文档类型" width="110" />
              <el-table-column prop="standard" label="审核标准" min-width="200" show-overflow-tooltip />
              <el-table-column prop="reason" label="违规原因" min-width="180" show-overflow-tooltip />
              <el-table-column prop="suggestion" label="处理建议" min-width="160" show-overflow-tooltip />
              <el-table-column label="实际值" min-width="160">
                <template slot-scope="{ row }">
                  <span v-if="row.actualValues" style="font-family:monospace;font-size:12px">
                    {{ row.actualValues }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <el-tab-pane label="LLM 配置快照" name="llm">
            <pre class="snap-pre">{{ formatJson(detail.llmConfigSnap) }}</pre>
          </el-tab-pane>

          <el-tab-pane label="检查项快照" name="items">
            <div style="margin-bottom:8px;color:#999">
              本次审核使用的全部检查项(用于复盘:即使后续修改了规则,这里仍是当时的版本)
            </div>
            <pre class="snap-pre">{{ formatJson(detail.checkItemSnap) }}</pre>
          </el-tab-pane>

          <el-tab-pane label="Prompt 快照" name="prompt">
            <div style="margin-bottom:8px">
              <el-button size="mini" icon="el-icon-document-copy" @click="copyPrompt">复制 Prompt</el-button>
            </div>
            <pre class="snap-pre">{{ detail.promptSnap }}</pre>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>
  </div>
  </div>
</template>

<script>
import { aiAuditApi } from './api.js';
import AiTabNav from './AiTabNav.vue';
import PageHeader from '@/components/PageHeader.vue';

const STATUS_TYPE = { PENDING: 'info', RUNNING: 'warning', SUCCESS: 'success', FAILED: 'danger' };
const OVERALL_TYPE = { PASS: 'success', WARN: 'warning', REJECT: 'danger' };

export default {
  name: 'AiAuditTask',
  components: { AiTabNav, PageHeader },
  data() {
    return {
      list: [],
      loading: false,
      total: 0,
      page: { pageNum: 1, pageSize: 10 },
      filter: { status: '', overallStatus: '', bizType: '', bizId: '' },
      detailVisible: false,
      activeTab: 'info',
      detail: null,
      results: [],
      llmConfigCache: {}  // id → 配置全量,判断是否是 vision 用
    };
  },
  computed: {
    /** 从 llmConfigSnap 解析模型信息 */
    llmSnapInfo() {
      if (!this.detail || !this.detail.llmConfigSnap) return {};
      try {
        return JSON.parse(this.detail.llmConfigSnap);
      } catch (e) {
        return {};
      }
    }
  },
  created() {
    this.loadList();
    this.loadLlmConfigs();
  },
  methods: {
    statusType(s) { return STATUS_TYPE[s] || ''; },
    overallType(s) { return OVERALL_TYPE[s] || ''; },

    /** 加载所有 LLM 配置索引,用于判断 task 是否是 vision 调用 */
    async loadLlmConfigs() {
      try {
        const { aiLlmApi } = await import('./api.js');
        const resp = await aiLlmApi.list();
        if (resp.code === 200 && resp.data) {
          const m = {};
          resp.data.forEach(c => { m[c.id] = c; });
          this.llmConfigCache = m;
        }
      } catch (e) { /* 忽略,有丢在表格里不显示标签而已 */ }
    },

    /** 判断该 task 是否使用了 vision 模型 */
    isVisionTask(row) {
      if (!row.llmConfigId) return false;
      const cfg = this.llmConfigCache[row.llmConfigId];
      return cfg && cfg.supportsVision === 1;
    },

    formatFeeTypes(s) {
      if (!s) return '-';
      try {
        const arr = JSON.parse(s);
        return Array.isArray(arr) ? arr.join('、') : s;
      } catch (e) { return s; }
    },

    formatJson(s) {
      if (!s) return '';
      try {
        return JSON.stringify(JSON.parse(s), null, 2);
      } catch (e) { return s; }
    },

    async loadList() {
      this.loading = true;
      try {
        const params = {
          pageNum: this.page.pageNum, pageSize: this.page.pageSize,
          ...this.filter
        };
        Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null) delete params[k]; });
        const resp = await aiAuditApi.pageTasks(params);
        if (resp.code === 200) {
          this.list = resp.data.records || [];
          this.total = resp.data.total || 0;
        } else {
          this.$message.error(resp.msg || '查询失败');
        }
      } catch (e) {
        this.$message.error('网络错误');
      } finally {
        this.loading = false;
      }
    },

    async openDetail(taskId) {
      this.detail = null;
      this.results = [];
      this.activeTab = 'info';
      this.detailVisible = true;
      try {
        const [taskResp, resultResp] = await Promise.all([
          aiAuditApi.getTask(taskId),
          aiAuditApi.getReport(taskId)
        ]);
        if (taskResp.code === 200) this.detail = taskResp.data;
        if (resultResp.code === 200) this.results = resultResp.data || [];
      } catch (e) {
        this.$message.error('加载详情失败');
      }
    },

    closeDetail(done) {
      this.detail = null;
      this.results = [];
      if (done) done();
    },

    copyPrompt() {
      if (!this.detail || !this.detail.promptSnap) return;
      const ta = document.createElement('textarea');
      ta.value = this.detail.promptSnap;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        this.$message.success('Prompt 已复制');
      } catch (e) {
        this.$message.error('复制失败');
      }
      document.body.removeChild(ta);
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-audit-task-page {
  padding: 20px;
  background: #fff;
  min-height: 100%;

  /* .page-header 已抽到 PageHeader 组件 */

  .pagination-wrap {
    margin-top: 16px;
    text-align: right;
  }

  .detail-body {
    padding: 0 24px;
  }

  .snap-pre {
    background: #2d2d2d;
    color: #d4d4d4;
    padding: 14px;
    border-radius: 6px;
    max-height: 540px;
    overflow: auto;
    font-family: Consolas, Monaco, monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
